import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Heart, Gift, CalendarHeart, PackageCheck, BookHeart } from "lucide-react";
import { api } from "@/lib/api";
import { useLang, dateLocale, notifText } from "@/lib/i18n";
import { Badge } from "@/components/ui/badge";
import { SmartImage } from "@/components/SmartImage";

export default function Memories() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/memories").then((res) => setData(res.data)).catch((err) => {
      console.error("memories load failed", err);
      setData({ past_events: [], received_thanks: [] });
    });
  }, []);

  if (!data) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const empty = data.past_events.length === 0 && data.received_thanks.length === 0;
  const locale = dateLocale(lang);
  const fmtDate = (d) => new Date(d).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });

  return (
    <div data-testid="memories-page">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-11 h-11 rounded-2xl bg-primary flex items-center justify-center">
          <BookHeart size={20} className="text-white" />
        </div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">{t("memories_title")}</h1>
          <p className="text-muted-foreground text-sm">{t("memories_sub")}</p>
        </div>
      </div>

      {empty && (
        <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center text-muted-foreground">
          <p className="text-4xl mb-3">📖</p>
          {t("no_memories")}
        </div>
      )}

      {data.past_events.length > 0 && (
        <div className="space-y-6 mb-10">
          {data.past_events.map((ev, idx) => (
            <div key={ev.wishlist_id} className="bg-card rounded-3xl border border-border/50 overflow-hidden fade-up" style={{ animationDelay: `${idx * 0.07}s` }} data-testid={`memory-card-${ev.wishlist_id}`}>
              <div className="flex flex-col sm:flex-row">
                {ev.cover_image && (
                  <img src={ev.cover_image} alt="" className="sm:w-56 h-36 sm:h-auto object-cover" />
                )}
                <div className="p-6 flex-1">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <Link to={`/liste/${ev.wishlist_id}`} className="font-heading text-lg font-bold hover:text-primary transition-colors">{ev.title}</Link>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        {ev.event_date && (
                          <span className="flex items-center gap-1"><CalendarHeart size={12} /> {fmtDate(ev.event_date)}</span>
                        )}
                        <span className="flex items-center gap-1"><Gift size={12} /> {ev.items.length} {t("gifts_n")}</span>
                        {ev.received_count > 0 && (
                          <span className="flex items-center gap-1 text-primary font-semibold"><PackageCheck size={12} /> {ev.received_count} {t("arrived_n")}</span>
                        )}
                        {ev.is_archived && <Badge variant="secondary" className="rounded-full text-[10px]">{t("archived_badge")}</Badge>}
                      </div>
                    </div>
                  </div>

                  {ev.items.length > 0 && (
                    <div className="flex gap-2 mt-4 overflow-x-auto pb-1">
                      {ev.items.slice(0, 8).map((it) => (
                        <div key={it.item_id} className="shrink-0 w-16" title={it.name}>
                          <div className="relative">
                            <SmartImage src={it.image_url} alt={it.name}
                              className={`w-16 h-16 object-cover rounded-xl ${it.received ? "" : "opacity-50 grayscale"}`}
                              fallback={<div className="w-16 h-16 rounded-xl bg-secondary flex items-center justify-center text-xl">🎁</div>} />
                            {it.received && (
                              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center">
                                <PackageCheck size={11} />
                              </span>
                            )}
                          </div>
                          <p className="text-[9px] text-muted-foreground truncate mt-1">{it.name}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {ev.thank_you_note && (
                    <div className="mt-4 bg-primary/5 border border-primary/15 rounded-xl p-3 flex gap-2">
                      <Heart size={14} className="text-primary shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm italic">"{ev.thank_you_note.message}"</p>
                        <p className="text-[10px] text-muted-foreground mt-1">{t("thanks_delivered", { n: ev.thank_you_note.recipient_count })}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {data.received_thanks.length > 0 && (
        <section>
          <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
            <Heart size={18} className="text-primary" /> {t("received_thanks")}
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {data.received_thanks.map((n) => (
              <div key={n.notification_id} className="bg-card rounded-2xl border border-border/50 p-4 flex gap-3" data-testid={`received-thank-${n.notification_id}`}>
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Heart size={15} className="text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm leading-snug">{notifText(t, n)}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{fmtDate(n.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
